import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { consumerApi, CONSUMER_TOKEN_KEY, CONSUMER_USER_KEY } from 'lib/consumer-api';
import { API_ENDPOINTS } from 'constants/endpoints';
import type { ConsumerPortalUser } from 'types/consumer';

interface LoginResponseData {
  token: string;
  consumer: ConsumerPortalUser;
}

interface ConsumerAuthApi {
  consumer: ConsumerPortalUser | null;
  token: string | null;
  login: (username: string, password: string) => Promise<{ ok: boolean; message?: string }>;
  logout: () => void;
  hasPermission: (moduleKey: string) => boolean;
}

const ConsumerAuthContext = createContext<ConsumerAuthApi | null>(null);

const readStoredConsumer = (): ConsumerPortalUser | null => {
  try {
    const raw = localStorage.getItem(CONSUMER_USER_KEY);
    return raw ? (JSON.parse(raw) as ConsumerPortalUser) : null;
  } catch {
    return null;
  }
};

export const ConsumerAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(CONSUMER_TOKEN_KEY));
  const [consumer, setConsumer] = useState<ConsumerPortalUser | null>(() => readStoredConsumer());

  React.useEffect(() => {
    const storedToken = localStorage.getItem(CONSUMER_TOKEN_KEY);
    if (!storedToken) return;

    consumerApi
      .get<ConsumerPortalUser>(API_ENDPOINTS.CONSUMER_PORTAL.AUTH.ME)
      .then((res) => {
        if (res.success && res.data) {
          localStorage.setItem(CONSUMER_USER_KEY, JSON.stringify(res.data));
          setConsumer(res.data);
        }
      })
      .catch(() => undefined);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    try {
      const res = await consumerApi.post<LoginResponseData>(API_ENDPOINTS.CONSUMER_PORTAL.AUTH.LOGIN, {
        username: username.trim().toLowerCase(),
        password,
      });
      const payload = res.data as LoginResponseData | undefined;

      if (res.success && payload?.token && payload?.consumer) {
        localStorage.setItem(CONSUMER_TOKEN_KEY, payload.token);
        localStorage.setItem(CONSUMER_USER_KEY, JSON.stringify(payload.consumer));
        setToken(payload.token);
        setConsumer(payload.consumer);
        return { ok: true };
      }

      return {
        ok: false,
        message:
          res.message === 'Unauthorized'
            ? 'Invalid username or password.'
            : res.message || 'Login failed.',
      };
    } catch {
      return { ok: false, message: 'Unable to reach the server. Please try again.' };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(CONSUMER_TOKEN_KEY);
    localStorage.removeItem(CONSUMER_USER_KEY);
    setToken(null);
    setConsumer(null);
  }, []);

  const hasPermission = useCallback(
    (moduleKey: string) => {
      if (!consumer) return false;
      return Array.isArray(consumer.permissions) && consumer.permissions.includes(moduleKey);
    },
    [consumer]
  );

  const value = useMemo<ConsumerAuthApi>(
    () => ({ consumer, token, login, logout, hasPermission }),
    [consumer, token, login, logout, hasPermission]
  );

  return <ConsumerAuthContext.Provider value={value}>{children}</ConsumerAuthContext.Provider>;
};

export const useConsumerAuth = (): ConsumerAuthApi => {
  const ctx = useContext(ConsumerAuthContext);
  if (!ctx) throw new Error('useConsumerAuth must be used within a ConsumerAuthProvider');
  return ctx;
};
